import { yupResolver } from "@hookform/resolvers/yup";
import { CourseEnrollmentContent } from "api/shop";
import { HeadingTagSelect, PaziaFormButton, PaziaInput, PaziaSelect } from "components/atoms";
import { PaziaSwitch } from "components/atoms/PaziaSwitch/PaziaSwitch";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";

interface CourseEnrollmentFormProps {
  onSubmit: (data: CourseEnrollmentFormValues) => void;
  onCancel: () => void;
  defaultValues?: CourseEnrollmentContent;
  isLoading?: boolean;
}

export const CourseEnrollmentForm = ({ onSubmit, onCancel, defaultValues, isLoading }: CourseEnrollmentFormProps) => {
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      variant: defaultValues?.variant ?? "mobile-inline",
      title: defaultValues?.title ?? "",
      titleTag: defaultValues?.titleTag ?? "h2",
      displayMode: defaultValues?.displayMode ?? "default",
      showCourseGroupName: defaultValues?.showCourseGroupName ?? true,
      showImage: defaultValues?.showImage ?? true,
      showCourseDates: defaultValues?.showCourseDates ?? true,
      showPrice: defaultValues?.showPrice ?? true,
      mobileSticky: defaultValues?.mobileSticky ?? false,
      showBorder: defaultValues?.showBorder ?? true,
      asideMarginTop: defaultValues?.asideMarginTop,
    },
  });

  const watchedVariant = methods.watch("variant");
  const watchedDisplayMode = methods.watch("displayMode");

  const handleFormSubmit = (data: FormValues) => {
    const formattedData: CourseEnrollmentFormValues = {
      variant: data.variant as "mobile-inline" | "desktop-sidebar",
      title: data.title || undefined,
      titleTag: data.titleTag as any,
      displayMode: (data.displayMode as "default" | "card") ?? "default",
      showCourseGroupName: data.showCourseGroupName,
      showImage: data.showImage,
      showCourseDates: data.showCourseDates,
      showPrice: data.showPrice,
      mobileSticky: data.mobileSticky,
      showBorder: data.showBorder,
    };
    if (data.variant === "desktop-sidebar" && data.asideMarginTop) {
      formattedData.asideMarginTop = Number(data.asideMarginTop);
    }
    onSubmit(formattedData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaSelect
          name="variant"
          label="Wariant"
          options={VARIANT_OPTIONS}
          required
        />
        <PaziaInput name="title" label="Tytuł (opcjonalnie)" placeholder="np. Zapisz się na kurs" />
        <HeadingTagSelect name="titleTag" label="Tag tytułu" />

        <PaziaSelect
          name="displayMode"
          label="Tryb wyświetlania"
          options={DISPLAY_MODE_OPTIONS}
        />

        <PaziaSwitch name="showCourseGroupName" label="Wyświetlaj nazwę grupy kursów" />
        <PaziaSwitch name="showImage" label="Wyświetlaj zdjęcie" />
        <PaziaSwitch name="showCourseDates" label="Wyświetlaj daty kursu" />
        <PaziaSwitch name="showPrice" label="Wyświetlaj cenę" />

        {watchedDisplayMode === "card" && (
          <PaziaSwitch name="showBorder" label="Wyświetlaj obrys karty (border + cień)" />
        )}

        {watchedVariant === "mobile-inline" && (
          <PaziaSwitch name="mobileSticky" label="Przyklej do dołu ekranu na mobile" />
        )}

        {watchedVariant === "desktop-sidebar" && (
          <PaziaInput name="asideMarginTop" label="Margines górny aside (px)" type="number" placeholder="0" />
        )}

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const VARIANT_OPTIONS = [
  { value: "mobile-inline", label: "Mobilny (inline)" },
  { value: "desktop-sidebar", label: "Desktop (sidebar)" },
];

const DISPLAY_MODE_OPTIONS = [
  { value: "default", label: "Domyślny (sidebar)" },
  { value: "card", label: "Karta produktowa" },
];

const schema = yup
  .object({
    variant: yup.string().required("Wariant jest wymagany"),
    title: yup.string().optional(),
    titleTag: yup.string().optional(),
    displayMode: yup.string().optional(),
    showCourseGroupName: yup.boolean().optional(),
    showImage: yup.boolean().optional(),
    showCourseDates: yup.boolean().optional(),
    showPrice: yup.boolean().optional(),
    mobileSticky: yup.boolean().optional(),
    showBorder: yup.boolean().optional(),
    asideMarginTop: yup
      .number()
      .transform((value, original) => (original === "" ? undefined : value))
      .optional(),
  })
  .required();

interface FormValues {
  variant: string;
  title?: string;
  titleTag?: string;
  displayMode?: string;
  showCourseGroupName?: boolean;
  showImage?: boolean;
  showCourseDates?: boolean;
  showPrice?: boolean;
  mobileSticky?: boolean;
  showBorder?: boolean;
  asideMarginTop?: number;
}

export interface CourseEnrollmentFormValues {
  variant: "mobile-inline" | "desktop-sidebar";
  title?: string;
  titleTag?: string;
  displayMode?: "default" | "card";
  showCourseGroupName?: boolean;
  showImage?: boolean;
  showCourseDates?: boolean;
  showPrice?: boolean;
  mobileSticky?: boolean;
  showBorder?: boolean;
  asideMarginTop?: number;
}
